/**
 * Signup attribution carried into the first `/me` call.
 *
 * Two things had to be fixed here (BUG-B):
 *
 * 1. The query string was concatenated by hand in App.jsx, so referral + coupon
 *    together produced `/me?registerReferral=Y?registerCoupon=X`. svc-proxy then
 *    read `registerReferral = "Y?registerCoupon=X"` and `registerCoupon = null`:
 *    the referrer was never found and the coupon fell through to INBOUNDWEB.
 *    `buildMeUrl` is the single place that assembles that URL now.
 *
 * 2. `?campaign=` / `?ma=` live only in `window.location.search`, and the
 *    redirect inside App's `maybeCheckAuth` carries pathname/hash only. Whenever
 *    that redirect fires the attribution is gone before `refreshUser()` reads
 *    it. The stash below keeps the pair in sessionStorage across that hop (and
 *    across a mid-flow reload) and is cleared only once `/me` has actually
 *    accepted them — clearing on read would lose the attribution to any reload,
 *    and never clearing would carry a coupon into the next login on the same
 *    tab.
 *
 * svc-proxy only ever gets ONE chance to record the attribution (the coupon is
 * written while the Xendit user is created, and only when the account has no
 * xenditUserId yet), which is why the stash errs on the side of keeping the
 * values rather than dropping them early.
 */

/** @type {string} sessionStorage key holding the stashed attribution pair. */
export const REGISTER_PARAMS_STORAGE_KEY = 'mayar:register-attribution';

/**
 * How long a stashed pair stays usable. Long enough to cover a sign-in bounce
 * plus a slow reload, short enough that a tab left open for an afternoon does
 * not attach yesterday's coupon to whoever logs in next.
 *
 * @type {number} milliseconds.
 */
export const REGISTER_PARAMS_TTL_MS = 30 * 60 * 1000;

const EMPTY_PARAMS = { registerReferral: '', registerCoupon: '' };

/**
 * Build the `/me` URL, appending only the attribution params that are present.
 *
 * @param {string} baseProxyUrl proxy origin (REACT_APP_PROXY), used verbatim.
 * @param {Object} [params] attribution pair.
 * @param {string} [params.registerReferral] referral code (`?ma=`).
 * @param {string} [params.registerCoupon] campaign/coupon code (`?campaign=`).
 *
 * @return {string} `<base>/me` with a single, properly encoded query string.
 */
export function buildMeUrl(baseProxyUrl, params = {}) {
  const { registerReferral, registerCoupon } = params;
  const search = new URLSearchParams();

  if (registerReferral) {
    search.set('registerReferral', registerReferral);
  }
  if (registerCoupon) {
    search.set('registerCoupon', registerCoupon);
  }

  const query = search.toString();

  return `${baseProxyUrl}/me${query ? `?${query}` : ''}`;
}

/**
 * Read the attribution pair out of a location search string.
 *
 * @param {string} [search] `window.location.search`.
 *
 * @return {Object} `{ registerReferral, registerCoupon }`, empty strings when absent.
 */
export function readRegisterParams(search) {
  const query = new URLSearchParams(search || '');

  return {
    registerReferral: query.get('ma') || '',
    registerCoupon: query.get('campaign') || ''
  };
}

/**
 * Remember the attribution pair for the rest of this tab's signup flow.
 * A pair with nothing in it writes nothing, so an empty visit cannot overwrite
 * a real one with a blank record.
 *
 * @param {Object} [params] attribution pair.
 * @param {string} [params.registerReferral] referral code.
 * @param {string} [params.registerCoupon] campaign/coupon code.
 */
export function stashRegisterParams(params = {}) {
  const registerReferral = params.registerReferral || '';
  const registerCoupon = params.registerCoupon || '';

  if (!registerReferral && !registerCoupon) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      REGISTER_PARAMS_STORAGE_KEY,
      JSON.stringify({ registerReferral, registerCoupon, at: Date.now() })
    );
  } catch {
    // Private mode / storage disabled: attribution degrades to "URL only".
  }
}

/**
 * Forget the stashed pair. Called only after `/me` has consumed it.
 */
export function clearStashedRegisterParams() {
  try {
    window.sessionStorage.removeItem(REGISTER_PARAMS_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Read the stashed pair back. Non-destructive on purpose — a reload before /me
 * succeeds must still find it. Expired or unreadable records are dropped.
 *
 * @return {Object} `{ registerReferral, registerCoupon }`, empty strings when absent.
 */
export function readStashedRegisterParams() {
  let raw = null;

  try {
    raw = window.sessionStorage.getItem(REGISTER_PARAMS_STORAGE_KEY);
  } catch {
    return { ...EMPTY_PARAMS };
  }

  if (!raw) {
    return { ...EMPTY_PARAMS };
  }

  let stored = null;
  try {
    stored = JSON.parse(raw);
  } catch {
    clearStashedRegisterParams();
    return { ...EMPTY_PARAMS };
  }

  if (!stored || typeof stored !== 'object' || Date.now() - stored.at > REGISTER_PARAMS_TTL_MS) {
    clearStashedRegisterParams();
    return { ...EMPTY_PARAMS };
  }

  return {
    registerReferral: stored.registerReferral || '',
    registerCoupon: stored.registerCoupon || ''
  };
}
